/**
 * Feature Flag Service
 *
 * Service for fetching feature flags from the API.
 * Supports authenticated requests for user/role-specific flags.
 */

import { FEATURE_FLAG_DEFAULTS } from '@court-sync/shared';
import { apiGet } from './apiClient';

/**
 * Get all feature flags
 * Returns user/role-specific flags if authenticated, otherwise global flags
 */
export const getFeatureFlags = async (): Promise<Record<string, boolean>> => {
  try {
    const response = await apiGet<Record<string, boolean>>('/feature-flags');

    if (response.success && response.data) {
      return response.data;
    }

    const defaults = FEATURE_FLAG_DEFAULTS || {};
    return { ...defaults };
  } catch (error: unknown) {
    console.error('Error fetching feature flags:', error);
    const defaults = FEATURE_FLAG_DEFAULTS || {};
    return { ...defaults };
  }
};

/**
 * Get a specific feature flag
 * Returns user/role-specific flag if authenticated, otherwise global flag
 */
export const getFeatureFlag = async (key: string): Promise<boolean> => {
  try {
    const response = await apiGet<{ key: string; enabled: boolean }>(`/feature-flags/${key}`);

    if (response.success && response.data) {
      return response.data.enabled;
    }

    const defaults = FEATURE_FLAG_DEFAULTS || {};
    return defaults[key as keyof typeof defaults] ?? false;
  } catch (error: unknown) {
    console.error(`Error fetching feature flag ${key}:`, error);
    const defaults = FEATURE_FLAG_DEFAULTS || {};
    return defaults[key as keyof typeof defaults] ?? false;
  }
};

export const featureFlagService = {
  getFeatureFlags,
  getFeatureFlag,
};
